/// <amd-module name="Feed/Legacy/Protocols/UPack/UPackDataService" />
import { IVssService, Services, VssService } from "VSS/Platform/Context";

import * as VSS_UPackApi_Contracts from "Feed/Common/Protocols/UPack/Contracts/UPackApi";
import { IUPackApiRestClient } from "Feed/Common/Protocols/UPack/RestClient/UPackApi";
import { HubWebPageDataService } from "Feed/Legacy/DataServices/WebPageDataService";
import { BatchExecutor, IBatchIterator } from "Feed/Legacy/Protocols/Common/BatchHandling";
import { MinimalPackageDetails } from "Feed/Legacy/Protocols/Common/WebApi/PackagingShared.Contracts";
import { Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

export interface IFeedUPackDataService extends IVssService {
    batchSize: number;
    batchDelete(feed: Feed, iterator: IBatchIterator<MinimalPackageDetails[]>): Promise<void>;
    batchRestoreToFeed(feed: Feed, iterator: IBatchIterator<MinimalPackageDetails[]>): Promise<void>;
    batchPermanentlyDelete(feed: Feed, iterator: IBatchIterator<MinimalPackageDetails[]>): Promise<void>;
    batchPromote(feed: Feed, viewId: string, iterator: IBatchIterator<MinimalPackageDetails[]>): Promise<void>;
}

class UPackDataService extends VssService implements IFeedUPackDataService {
    get UPackApiRestClient(): IUPackApiRestClient {
        return this.pageContext.getRestClient<IUPackApiRestClient>("Universal.IUPackApiRestClient");
    }

    get batchSize(): number {
        return this.pageContext
            .getService<HubWebPageDataService>("IFeedWebPageDataService")
            .getBatchOperationPageSize();
    }

    public async batchDelete(feed: Feed, iterator: IBatchIterator<MinimalPackageDetails[]>): Promise<void> {
        await BatchExecutor.executeInParallel(iterator, async (packageDetails: MinimalPackageDetails[]) => {
            const batchRequest = <VSS_UPackApi_Contracts.UPackPackagesBatchRequest>{
                data: null,
                operation: VSS_UPackApi_Contracts.UPackBatchOperationType.Delete,
                packages: packageDetails
            };
            const projectId = feed.project ? feed.project.id : null;
            await this.UPackApiRestClient.updatePackageVersions(batchRequest, feed.id, projectId);
        });
    }

    public async batchRestoreToFeed(feed: Feed, iterator: IBatchIterator<MinimalPackageDetails[]>): Promise<void> {
        await BatchExecutor.executeInParallel(iterator, async (packageDetails: MinimalPackageDetails[]) => {
            const batchRequest = <VSS_UPackApi_Contracts.UPackPackagesBatchRequest>{
                data: null,
                operation: VSS_UPackApi_Contracts.UPackBatchOperationType.RestoreToFeed,
                packages: packageDetails
            };
            const projectId = feed.project ? feed.project.id : null;
            await this.UPackApiRestClient.updateRecycleBinPackageVersions(batchRequest, feed.id, projectId);
        });
    }

    public async batchPermanentlyDelete(feed: Feed, iterator: IBatchIterator<MinimalPackageDetails[]>): Promise<void> {
        await BatchExecutor.executeInParallel(iterator, async (packageDetails: MinimalPackageDetails[]) => {
            const batchRequest = <VSS_UPackApi_Contracts.UPackPackagesBatchRequest>{
                data: null,
                operation: VSS_UPackApi_Contracts.UPackBatchOperationType.PermanentDelete,
                packages: packageDetails
            };
            const projectId = feed.project ? feed.project.id : null;
            await this.UPackApiRestClient.updateRecycleBinPackageVersions(batchRequest, feed.id, projectId);
        });
    }

    public async batchPromote(
        feed: Feed,
        viewId: string,
        iterator: IBatchIterator<MinimalPackageDetails[]>
    ): Promise<void> {
        await BatchExecutor.executeInParallel(iterator, async (packageDetails: MinimalPackageDetails[]) => {
            const batchRequest = <VSS_UPackApi_Contracts.UPackPackagesBatchRequest>{
                data: { viewId },
                operation: VSS_UPackApi_Contracts.UPackBatchOperationType.Promote,
                packages: packageDetails
            };
            const projectId = feed.project ? feed.project.id : null;
            await this.UPackApiRestClient.updatePackageVersions(batchRequest, feed.id, projectId);
        });
    }
}

Services.add("IFeedUPackDataService", { serviceFactory: UPackDataService });
